import toast from 'react-hot-toast'
import { Middleware } from 'openapi-fetch'

import { TokenWithPermissions } from '@/domains/auth/domain/models/TokenWithPermissions'
import { authService } from '@/features/Auth/infrastructure/services/authService'

const UNPROTECTED_ROUTES = ['/v1/authorize/refresh', '/v1/authorize']
const LOGIN_ROUTE = '/auth/login'

let refreshPromise: Promise<TokenWithPermissions> | null = null
let isRefreshing = false
const requestClones = new WeakMap()
const retryAttempts = new WeakMap()

const isUnprotectedRoute = (schemaPath: string): boolean => {
  return UNPROTECTED_ROUTES.some((pathname) => schemaPath.startsWith(pathname))
}

const cleanupRequest = (request: Request): void => {
  requestClones.delete(request)
  retryAttempts.delete(request)
}

const clearAllStorage = (): void => {
  localStorage.clear()
  sessionStorage.clear()
}

const initializeRefreshProcess = (): Promise<TokenWithPermissions> => {
  if (!isRefreshing) {
    isRefreshing = true
    refreshPromise = authService
      .getTokens({ forceRefresh: true })
      .then((tokens) => {
        isRefreshing = false
        return tokens.accessToken
      })
      .catch((error) => {
        isRefreshing = false
        refreshPromise = null
        throw error
      })
  }
  return refreshPromise!
}

const retryRequestWithNewToken = async (
  request: Request,
  accessToken: TokenWithPermissions,
): Promise<Response> => {
  const clonedRequest = requestClones.get(request)

  if (!clonedRequest || !accessToken) {
    console.error('No cloned request available for retry')
    throw new Error('Cannot retry request')
  }

  clonedRequest.headers.set('Authorization', `Bearer ${accessToken.value}`)

  try {
    const result = await fetch(clonedRequest)
    cleanupRequest(request)
    return result
  } catch (retryError) {
    console.error('Retry fetch failed (not refresh error):', retryError)
    cleanupRequest(request)
    throw retryError
  }
}

const handleRefreshFailure = async (
  request: Request,
  error: Error,
): Promise<void> => {
  console.error('Refresh token failed:', error)
  cleanupRequest(request)

  try {
    await authService.logout()
    clearAllStorage()
  } catch (logoutError) {
    console.error('Logout failed:', logoutError)
    clearAllStorage()
  }

  toast.error('Session expired. Please sign in again.')
  window.location.href = LOGIN_ROUTE
}

const handleUnauthorizedResponse = async (
  response: Response,
  request: Request,
): Promise<Response> => {
  if (!(response.status === 401 && !retryAttempts.get(request))) {
    return response
  }

  retryAttempts.set(request, true)

  try {
    const refreshPromise = initializeRefreshProcess()
    const accessToken = await refreshPromise
    return await retryRequestWithNewToken(request, accessToken)
  } catch (refreshError) {
    const error =
      refreshError instanceof Error
        ? refreshError
        : new Error(String(refreshError))
    await handleRefreshFailure(request, error)
    return response
  }
}

const handleForbiddenResponse = (response: Response): Response => {
  toast.error("Access denied. You don't have permission for this action.")
  return response
}

export const authMiddleware: Middleware = {
  async onRequest({ schemaPath, request }) {
    request.headers.set('Content-Type', 'application/json')

    if (isUnprotectedRoute(schemaPath)) {
      return undefined
    }

    try {
      const { accessToken } = await authService.getTokens()

      request.headers.set('Authorization', `Bearer ${accessToken.value}`)

      try {
        requestClones.set(request, request.clone())
      } catch (e) {
        console.warn('Could not clone request:', e)
      }

      return request
    } catch (refreshError) {
      console.error('Refresh token failed:', refreshError)
      cleanupRequest(request)
      const error =
        refreshError instanceof Error
          ? refreshError
          : new Error(String(refreshError))
      await handleRefreshFailure(request, error)
    }
  },

  async onResponse({ response, request, schemaPath }) {
    if (isUnprotectedRoute(schemaPath)) {
      return response
    }

    if (response.status === 401) {
      return await handleUnauthorizedResponse(response, request)
    }

    if (response.status === 403) {
      return handleForbiddenResponse(response)
    }

    return response
  },
}
